/**
 * Dark Mode Theme Provider and Accessibility Enhancements
 * Supports color schemes, light/dark mode, and accessibility features
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getThemePreferences, updateThemePreferences } from '@/lib/api';

const ThemeContext = createContext(null);

// Color scheme definitions (accent colors)
const colorSchemes = {
  teal: { name: 'Teal', primary: '#0D9488', secondary: '#14B8A6', accent: '#2DD4BF', ring: '#0D9488' },
  sage: { name: 'Sage Green', primary: '#78A55A', secondary: '#84CC16', accent: '#A3E635', ring: '#78A55A' },
  lavender: { name: 'Lavender', primary: '#9F7AEA', secondary: '#A855F7', accent: '#C084FC', ring: '#9F7AEA' },
  warm: { name: 'Warm Sunset', primary: '#DD6B20', secondary: '#EA580C', accent: '#FB923C', ring: '#DD6B20' },
  ocean: { name: 'Ocean Blue', primary: '#3182CE', secondary: '#2563EB', accent: '#60A5FA', ring: '#3182CE' },
  rose: { name: 'Rose', primary: '#DB2777', secondary: '#EC4899', accent: '#F472B6', ring: '#DB2777' },
  slate: { name: 'Slate Gray', primary: '#475569', secondary: '#64748B', accent: '#94A3B8', ring: '#475569' },
  emerald: { name: 'Emerald', primary: '#059669', secondary: '#10B981', accent: '#34D399', ring: '#059669' },
  amber: { name: 'Amber Gold', primary: '#D97706', secondary: '#F59E0B', accent: '#FBBF24', ring: '#D97706' },
  indigo: { name: 'Indigo Night', primary: '#4F46E5', secondary: '#6366F1', accent: '#818CF8', ring: '#4F46E5' },
  coral: { name: 'Coral Reef', primary: '#F43F5E', secondary: '#FB7185', accent: '#FDA4AF', ring: '#F43F5E' },
  forest: { name: 'Forest Pine', primary: '#166534', secondary: '#22C55E', accent: '#4ADE80', ring: '#166534' },
};

// Theme configurations (light/dark base)
const themes = {
  light: {
    name: 'Light',
    colors: {
      background: '#ffffff',
      foreground: '#0f172a',
      card: '#ffffff',
      cardForeground: '#0f172a',
      popover: '#ffffff',
      popoverForeground: '#0f172a',
      primary: '#0d9488',
      primaryForeground: '#ffffff',
      secondary: '#f1f5f9',
      secondaryForeground: '#0f172a',
      muted: '#f1f5f9',
      mutedForeground: '#64748b',
      accent: '#f1f5f9',
      accentForeground: '#0f172a',
      destructive: '#ef4444',
      destructiveForeground: '#ffffff',
      border: '#e2e8f0',
      input: '#e2e8f0',
      ring: '#0d9488',
    }
  },
  dark: {
    name: 'Dark',
    colors: {
      background: '#0f172a',
      foreground: '#f8fafc',
      card: '#1e293b',
      cardForeground: '#f8fafc',
      popover: '#1e293b',
      popoverForeground: '#f8fafc',
      primary: '#14b8a6',
      primaryForeground: '#0f172a',
      secondary: '#334155',
      secondaryForeground: '#f8fafc',
      muted: '#334155',
      mutedForeground: '#94a3b8',
      accent: '#334155',
      accentForeground: '#f8fafc',
      destructive: '#dc2626',
      destructiveForeground: '#f8fafc',
      border: '#334155',
      input: '#334155',
      ring: '#14b8a6',
    }
  },
  highContrast: {
    name: 'High Contrast',
    colors: {
      background: '#000000',
      foreground: '#ffffff',
      card: '#000000',
      cardForeground: '#ffffff',
      popover: '#000000',
      popoverForeground: '#ffffff',
      primary: '#00ff00',
      primaryForeground: '#000000',
      secondary: '#1a1a1a',
      secondaryForeground: '#ffffff',
      muted: '#1a1a1a',
      mutedForeground: '#cccccc',
      accent: '#ffff00',
      accentForeground: '#000000',
      destructive: '#ff0000',
      destructiveForeground: '#ffffff',
      border: '#ffffff',
      input: '#333333',
      ring: '#00ff00',
    }
  }
};

// Accessibility settings
const defaultAccessibility = {
  fontSize: 'medium', // small, medium, large, xlarge
  reduceMotion: false,
  reduceTransparency: false,
  screenReaderMode: false,
  keyboardNavigation: true,
  colorBlindMode: 'none', // none, deuteranopia, protanopia, tritanopia
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState('light');
  const [colorScheme, setColorSchemeState] = useState('teal');
  const [highContrast, setHighContrastState] = useState(false);
  const [accessibility, setAccessibility] = useState(defaultAccessibility);
  const [systemPrefersDark, setSystemPrefersDark] = useState(false);
  const [useSystemTheme, setUseSystemTheme] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncTimeoutRef = useRef(null);

  // Load preferences from localStorage first (instant), then sync with backend if logged in
  useEffect(() => {
    const savedTheme = localStorage.getItem('goodwin-theme');
    const savedColorScheme = localStorage.getItem('goodwin-color-scheme');
    const savedAccessibility = localStorage.getItem('goodwin-accessibility');
    const savedUseSystem = localStorage.getItem('goodwin-use-system-theme');
    const savedHighContrast = localStorage.getItem('goodwin-high-contrast');

    // If no saved preferences exist, default to light theme (not system preference)
    if (savedTheme) {
      setTheme(savedTheme);
    } else {
      // First-time user: default to light theme
      setTheme('light');
      setUseSystemTheme(false);
      localStorage.setItem('goodwin-theme', 'light');
      localStorage.setItem('goodwin-use-system-theme', 'false');
    }
    
    // Load color scheme
    if (savedColorScheme && colorSchemes[savedColorScheme]) {
      setColorSchemeState(savedColorScheme);
    }
    
    if (savedAccessibility) setAccessibility(JSON.parse(savedAccessibility));
    if (savedUseSystem !== null) setUseSystemTheme(savedUseSystem === 'true');
    if (savedHighContrast !== null) setHighContrastState(savedHighContrast === 'true');

    // Check system preference (still track it for users who opt-in)
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemPrefersDark(mediaQuery.matches);

    const handler = (e) => setSystemPrefersDark(e.matches);
    mediaQuery.addEventListener('change', handler);
    
    // Sync with backend if user is logged in
    const token = localStorage.getItem('hospice_token');
    if (token) {
      syncFromBackendInternal();
    }
    
    // Listen for login events to sync theme
    const handleLogin = () => {
      syncFromBackendInternal();
    };
    window.addEventListener('auth-login', handleLogin);
    
    return () => {
      mediaQuery.removeEventListener('change', handler);
      window.removeEventListener('auth-login', handleLogin);
    };
  }, []);

  // Internal sync function (non-callback version for use in useEffect)
  const syncFromBackendInternal = async () => {
    try {
      const token = localStorage.getItem('hospice_token');
      if (!token) return;
      
      const response = await getThemePreferences();
      const prefs = response.theme_preferences;
      
      if (prefs) {
        // Apply dark_mode preference from backend
        if (prefs.dark_mode === 'system') {
          setUseSystemTheme(true);
        } else if (prefs.dark_mode === 'light' || prefs.dark_mode === 'dark') {
          setUseSystemTheme(false);
          setTheme(prefs.dark_mode);
        }
        
        // Apply color scheme
        if (prefs.color_scheme && colorSchemes[prefs.color_scheme]) {
          setColorSchemeState(prefs.color_scheme);
          localStorage.setItem('goodwin-color-scheme', prefs.color_scheme);
        }
        
        // Apply high contrast
        if (prefs.high_contrast !== undefined) {
          setHighContrastState(prefs.high_contrast);
        }
        
        // Apply accessibility settings
        if (prefs.reduce_animations !== undefined) {
          setAccessibility(prev => ({ ...prev, reduceMotion: prefs.reduce_animations }));
        }
        if (prefs.font_size) {
          setAccessibility(prev => ({ ...prev, fontSize: prefs.font_size }));
        }
      }
    } catch (error) {
      // Silently fail - user might not be logged in or token expired
      console.debug('Could not sync theme from backend:', error.message);
    }
  };

  // Sync theme preferences from backend (callback version for external use)
  const syncFromBackend = useCallback(async () => {
    await syncFromBackendInternal();
  }, []);

  // Save theme preferences to backend (debounced)
  const syncToBackend = useCallback(async (darkMode, highContrastVal, colorSchemeVal = null) => {
    try {
      const token = localStorage.getItem('hospice_token');
      if (!token) return;
      
      setIsSyncing(true);
      await updateThemePreferences(colorSchemeVal, null, highContrastVal, null, darkMode);
    } catch (error) {
      console.debug('Could not sync theme to backend:', error.message);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  // Apply theme to document
  useEffect(() => {
    const activeTheme = highContrast 
      ? 'highContrast'
      : useSystemTheme 
        ? (systemPrefersDark ? 'dark' : 'light')
        : theme;
    
    const themeConfig = themes[activeTheme];
    const colorConfig = colorSchemes[colorScheme] || colorSchemes.teal;
    const root = document.documentElement;

    // Apply base theme CSS variables
    Object.entries(themeConfig.colors).forEach(([key, value]) => {
      const cssVar = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVar, value);
    });

    // Apply color scheme (primary colors) - override base theme primary
    if (!highContrast) {
      root.style.setProperty('--primary', colorConfig.primary);
      root.style.setProperty('--primary-foreground', activeTheme === 'dark' ? '#0f172a' : '#ffffff');
      root.style.setProperty('--ring', colorConfig.ring);
      
      // Set CSS custom properties for the color scheme
      root.style.setProperty('--theme-primary', colorConfig.primary);
      root.style.setProperty('--theme-secondary', colorConfig.secondary);
      root.style.setProperty('--theme-accent', colorConfig.accent);
    }

    // Apply theme class
    root.classList.remove('light', 'dark', 'high-contrast');
    root.classList.add(activeTheme === 'highContrast' ? 'high-contrast' : activeTheme);
    
    // Apply color scheme class
    Object.keys(colorSchemes).forEach(scheme => root.classList.remove(`theme-${scheme}`));
    root.classList.add(`theme-${colorScheme}`);

    // Save preference
    localStorage.setItem('goodwin-theme', theme);
    localStorage.setItem('goodwin-color-scheme', colorScheme);
    localStorage.setItem('goodwin-use-system-theme', String(useSystemTheme));
    localStorage.setItem('goodwin-high-contrast', String(highContrast));
  }, [theme, colorScheme, useSystemTheme, systemPrefersDark, highContrast]);

  // Apply accessibility settings
  useEffect(() => {
    const root = document.documentElement;

    // Font size
    const fontSizes = {
      small: '14px',
      medium: '16px',
      large: '18px',
      xlarge: '20px'
    };
    root.style.setProperty('--base-font-size', fontSizes[accessibility.fontSize]);

    // Reduce motion
    if (accessibility.reduceMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }

    // Color blind modes
    root.classList.remove('deuteranopia', 'protanopia', 'tritanopia');
    if (accessibility.colorBlindMode !== 'none') {
      root.classList.add(accessibility.colorBlindMode);
    }

    // Save accessibility settings
    localStorage.setItem('goodwin-accessibility', JSON.stringify(accessibility));
  }, [accessibility]);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    setUseSystemTheme(false);
    
    // Debounce sync to backend
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    syncTimeoutRef.current = setTimeout(() => {
      syncToBackend(newTheme, highContrast);
    }, 500);
  }, [theme, highContrast, syncToBackend]);

  const setThemeMode = useCallback((mode) => {
    setTheme(mode);
    setUseSystemTheme(false);
    
    // Sync to backend
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    syncTimeoutRef.current = setTimeout(() => {
      syncToBackend(mode, highContrast);
    }, 500);
  }, [highContrast, syncToBackend]);

  const setSystemThemeMode = useCallback((enabled) => {
    setUseSystemTheme(enabled);
    
    // Sync to backend
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    syncTimeoutRef.current = setTimeout(() => {
      syncToBackend(enabled ? 'system' : theme, highContrast);
    }, 500);
  }, [theme, highContrast, syncToBackend]);

  const updateAccessibility = useCallback((updates) => {
    setAccessibility(prev => ({ ...prev, ...updates }));
  }, []);

  const setHighContrast = useCallback((enabled) => {
    setHighContrastState(enabled);
    
    // Sync to backend
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    syncTimeoutRef.current = setTimeout(() => {
      const darkMode = useSystemTheme ? 'system' : theme;
      syncToBackend(darkMode, enabled, colorScheme);
    }, 500);
  }, [useSystemTheme, theme, colorScheme, syncToBackend]);

  const setColorScheme = useCallback((scheme) => {
    if (!colorSchemes[scheme]) return;
    
    setColorSchemeState(scheme);
    
    // Sync to backend
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    syncTimeoutRef.current = setTimeout(() => {
      const darkMode = useSystemTheme ? 'system' : theme;
      syncToBackend(darkMode, highContrast, scheme);
    }, 500);
  }, [useSystemTheme, theme, highContrast, syncToBackend]);

  const value = {
    theme: useSystemTheme ? (systemPrefersDark ? 'dark' : 'light') : theme,
    setTheme: setThemeMode,
    toggleTheme,
    useSystemTheme,
    setUseSystemTheme: setSystemThemeMode,
    colorScheme,
    setColorScheme,
    colorSchemes,
    highContrast,
    setHighContrast,
    accessibility,
    updateAccessibility,
    themes,
    isDark: (useSystemTheme ? systemPrefersDark : theme === 'dark'),
    isSyncing,
    syncFromBackend,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

export default ThemeContext;
